<template>
  <Transition name="toast">
    <div v-if="visible" class="toast-container">
      <div class="toast" :class="typeClass">
        <div class="toast-content">
          <span class="toast-message">{{ message }}</span>
          <button class="toast-close" @click="close" aria-label="Close">
            ×
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'ToastNotification',
  props: {
    message: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'info', 'warning'].includes(value),
    },
    duration: {
      type: Number,
      default: 5000, // 5 seconds
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      timeoutId: null,
    };
  },
  computed: {
    typeClass() {
      return `toast-${this.type}`;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.open();
      } else {
        this.close();
      }
    },
  },
  mounted() {
    if (this.show) {
      this.open();
    }
  },
  beforeUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  methods: {
    open() {
      this.visible = true;
      if (this.duration > 0) {
        this.timeoutId = setTimeout(() => {
          this.close();
        }, this.duration);
      }
    },
    close() {
      this.visible = false;
      this.$emit('close');
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    },
  },
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 9999;
  max-width: 500px;
}

.toast {
  background: white;
  border: 2px solid;
  padding: 1rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
}

.toast-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.toast-message {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #1a1a1a;
  flex: 1;
}

.toast-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #1a1a1a;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.toast-close:hover {
  opacity: 0.7;
}

/* Toast types */
.toast-success {
  background-color: #d1fae5;
  border-color: #10b981;
}

.toast-error {
  background-color: #fee2e2;
  border-color: #ef4444;
}

.toast-info {
  background-color: #dbeafe;
  border-color: #2e4eed;
}

.toast-warning {
  background-color: #fef3c7;
  border-color: #f59e0b;
}

/* Animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateY(-100px);
  opacity: 0;
}

.toast-leave-to {
  transform: translateY(-100px);
  opacity: 0;
}

@media (max-width: 768px) {
  .toast-container {
    top: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
  }
}
</style>
